import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { DocumentSection } from "@/components/EventDocBlocks";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { EventDocPDF } from "@/pdf/EventDocPDF";
import { ArrowLeft, Download, FileText } from "lucide-react";

/**
 * Public document viewer. One route: /documents/:slug.
 * - Auth-gated (401 → /login).
 * - Renders sections/blocks using shared components.
 * - PDF download via @react-pdf/renderer.
 */
export default function EventDocViewer() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav(`/login?next=/documents/${slug}`); return; }
    setLoading(true);
    api.get(`/event-docs/${slug}`)
      .then(({ data }) => setDoc(data.document))
      .catch((e) => setError(e?.response?.status === 404 ? "Not found" : "Error loading document"))
      .finally(() => setLoading(false));
  }, [slug, user, authLoading, nav]);

  if (authLoading || loading) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading document…</div></section></Layout>;
  }
  if (error || !doc) {
    return (
      <Layout>
        <section className="container-summit py-24 max-w-xl text-center">
          <h1 className="serif text-3xl">Document not available</h1>
          <p className="mt-4 text-zinc-500">This document may not be published yet.</p>
          <Link to="/documents" className="btn-black mt-8 inline-flex" data-testid="doc-back-to-index">
            Back to Documents
          </Link>
        </section>
      </Layout>
    );
  }

  const fileName = `${doc.slug}-tl-supplier-summit-2026.pdf`;

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="container-summit py-14 md:py-20 max-w-4xl">
          <Link to="/documents" className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white inline-flex items-center gap-2" data-testid="doc-back">
            <ArrowLeft size={12} /> All Documents
          </Link>
          <div className="overline text-white/60 mt-8"><span className="accent-rule" />Official Document</div>
          <h1 className="serif text-4xl sm:text-5xl md:text-6xl mt-4 leading-[1.05]" data-testid="doc-title">{doc.title}</h1>
          {doc.subtitle ? (
            <p className="mt-5 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed" data-testid="doc-subtitle">{doc.subtitle}</p>
          ) : null}

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <PDFDownloadLink
              document={<EventDocPDF doc={doc} />}
              fileName={fileName}
              className="btn-black h-11 px-5 text-xs inline-flex items-center gap-2 bg-[#A39171] border-[#A39171] hover:bg-[#8f7f61] hover:border-[#8f7f61]"
              data-testid="doc-download-pdf"
            >
              {({ loading: l }) => (<><Download size={14} /> {l ? "Preparing PDF…" : "Download PDF"}</>)}
            </PDFDownloadLink>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50">
              Friday, September 11, 2026 · Travel + Leisure HQ
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="container-summit py-14 md:py-20 max-w-4xl">
        <div className="space-y-12">
          {(doc.sections || []).map((s) => (
            <DocumentSection key={s.id} section={s} />
          ))}
        </div>

        {/* Footer accent */}
        <div className="mt-16 border-t border-zinc-200 pt-8 flex flex-wrap items-center justify-between gap-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            Produced by Display America
          </div>
          <PDFDownloadLink
            document={<EventDocPDF doc={doc} />}
            fileName={fileName}
            className="text-[11px] uppercase tracking-[0.22em] text-black inline-flex items-center gap-2 hover:text-[#A39171]"
            data-testid="doc-download-pdf-footer"
          >
            {({ loading: l }) => (<><Download size={12} /> {l ? "Preparing PDF…" : "Download PDF"}</>)}
          </PDFDownloadLink>
        </div>
      </section>
    </Layout>
  );
}
